import { useEffect, useMemo, useRef, useState } from 'react';

import { SongPreview } from '~/interfaces';
import events from '~/modules/game-events/game-events';
import { useEventEffect, useEventListenerSelector } from '~/modules/game-events/hooks';
import PlayersManager from '~/modules/players/players-manager';
import useNoteIndex from '~/modules/songs/hooks/use-note-index';
import { getAllStats } from '~/modules/songs/stats/common';
import { useProfiles } from '~/modules/user-profiles/hooks';
import { playsForName } from '~/modules/user-profiles/profile-stats';
import { normaliseProfileName, UserProfile } from '~/modules/user-profiles/profiles';
import { recommendSongs, tasteFromPlays } from '~/modules/user-profiles/recommendations';

/** Enough to fill a playlist without the tail becoming noise. */
const RECOMMENDATION_LIMIT = 150;

/**
 * Songs suggested for whoever is actually about to sing - the profiles sitting on player slots.
 * Layered alongside upstream's popularity-based Selection rather than replacing it: this one only
 * ever speaks for a profile with a history, and says nothing at all when there is none.
 */
export default function usePersonalRecommendations(songs: SongPreview[]) {
  const profiles = useProfiles();
  const { noteIndex } = useNoteIndex();

  const playerNames = useEventListenerSelector([events.playerNameChanged, events.playerInputChanged], () =>
    PlayersManager.getPlayers()
      .map((player) => player.getName())
      .join('\n'),
  );

  const assigned: UserProfile[] = useMemo(() => {
    const names = new Set((playerNames ?? '').split('\n').map(normaliseProfileName));

    return profiles.filter((profile) => names.has(normaliseProfileName(profile.name)));
  }, [profiles, playerNames]);

  const [songIds, setSongIds] = useState<string[]>([]);
  const [forProfiles, setForProfiles] = useState<UserProfile[]>([]);

  // Keyed by name so the effect re-runs when who is playing changes, not on every render.
  const assignedKey = assigned.map((profile) => profile.name).join('\n');

  /**
   * Which run is the current one. Two things ask for a reload - who is playing changing, and a song
   * being recorded - and both can fire while a read of the stats is still in flight. Without this
   * the slower read wins whenever it happens to finish last, and the list ends up describing
   * whoever was playing a moment ago.
   */
  const generation = useRef(0);

  const load = useMemo(
    () => async () => {
      const run = ++generation.current;

      if (assigned.length === 0) {
        setSongIds([]);
        setForProfiles([]);
        return;
      }

      const stats = await getAllStats();
      const songsById = new Map(songs.map((song) => [song.id, song]));

      // One ranking across everyone singing, so a duet pair gets songs that suit the pair rather
      // than two competing lists.
      const scores = new Map<string, number>();
      for (const profile of assigned) {
        // The profile's own measured range, when they have run the detector - without it the range
        // signal simply does not apply to them.
        const taste = tasteFromPlays(playsForName(profile.name, stats), songsById, noteIndex, profile.vocalRange);
        for (const { song, score } of recommendSongs(songs, taste, RECOMMENDATION_LIMIT, noteIndex)) {
          scores.set(song.id, (scores.get(song.id) ?? 0) + score);
        }
      }

      if (run !== generation.current) return;

      setSongIds(
        [...scores.entries()]
          .sort(([, a], [, b]) => b - a)
          .slice(0, RECOMMENDATION_LIMIT)
          .map(([id]) => id),
      );
      setForProfiles(assigned);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `assignedKey` stands in for `assigned`
    [assignedKey, songs, noteIndex],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // A song just sung changes what is worth suggesting next.
  useEventEffect(events.songStatStored, () => void load(), [load]);

  // Stable identity: this feeds a `useMemo` dependency list in `usePlaylists`, and a fresh object
  // each render would rebuild every playlist continuously.
  return useMemo(() => ({ songIds, forProfiles }), [songIds, forProfiles]);
}
